<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import { CheckCircleIcon } from '@heroicons/vue/20/solid'
import HeadingTwo from '@/Components/HeadingTwo.vue'
import HeadingThree from '@/Components/HeadingThree.vue'

const taxDiscounts = [
    {
        title: 'Sleva na poplatníka',
        amount: '30 840 Kč ročně',
        condition: 'Uplatňuje každý daňový poplatník bez ohledu na výši příjmů.',
    },
    {
        title: 'Sleva na manžela/manželku',
        amount: '24 840 Kč ročně',
        conditions: [
            'Manžel/ka nemá vlastní příjem přesahující 68 000 Kč ročně.',
            'Nově je nutné, aby manžel/ka pečoval/a o dítě do 3 let věku ve společné domácnosti.',
            'Sleva se často uplatňuje u rodičů na rodičovské dovolené.',
        ],
    },
    {
        title: 'Sleva na invaliditu',
        amounts: ['2 520 Kč ročně (pro invalidní důchod 1. a 2. stupně).', '5 040 Kč ročně (pro invalidní důchod 3. stupně).'],
        condition: 'Držitel invalidního důchodu musí doložit potvrzení o přiznání důchodu.',
    },
    {
        title: 'Sleva na držitele průkazu ZTP/P',
        amount: '16 140 Kč ročně',
        condition: 'Nutné doložit průkaz ZTP/P.',
    },
    {
        title: 'Daňové zvýhodnění na dítě (tzv. daňový bonus)',
        amounts: ['15 204 Kč ročně (první dítě).', '22 320 Kč ročně (druhé dítě).', '27 840 Kč ročně (třetí a každé další dítě).'],
        conditions: [
            'Poplatník má na dítě vyživovací povinnost (typicky rodiče).',
            'Dítě musí být nezaopatřené (např. studující).',
            'Uplatňuje jen jeden z rodičů.',
        ],

        note: 'Pokud daňové zvýhodnění přesáhne výši vypočítané daně, vzniká daňový bonus – stát peníze vrátí poplatníkovi.',
    },
]

const taxDeductions = [
    {
        title: 'Úroky z úvěru na bydlení (hypotéka nebo stavební spoření)',
        amount: 'Až 150 000 Kč ročně',
        conditions: [
            'Úvěr musí být použit na bytovou potřebu (např. koupě, výstavba, rekonstrukce).',
            'Je nutné doložit potvrzení o zaplacených úrocích od poskytovatele úvěru.',
        ],
    },
    {
        title: 'Dary na veřejně prospěšné účely',
        amount: 'Minimálně 1 000 Kč ročně, maximálně do 30 % základu daně',
        conditions: [
            'Dary mohou být poskytnuty na charitu, kulturu, vzdělávání, zdravotnictví apod.',
            'Je nutné doložit potvrzení o daru.',
        ],
    },
    {
        title: 'Penzijní připojištění nebo doplňkové penzijní spoření',
        amount: 'Až 24 000 Kč ročně',
        condition: 'Odečítá se částka nad 12 000 Kč ročně (tedy příspěvky nad tuto hranici). Potvrzení od penzijní společnosti je povinné.',
    },
    {
        title: 'Životní pojištění',
        amount: 'Až 24 000 Kč ročně',
        condition: 'Platí pro pojistné na soukromé životní pojištění s výplatou až po 60 letech.',
    },
]

const changes2024 = [
    'Zrušení slevy na studenta: Tato sleva byla k 1. 1. 2024 zrušena.',
    'Zrušení slevy za umístění dítěte (školkovné): Tato sleva byla k 1. 1. 2024 zrušena.',
    'Omezení slevy na manžela/manželku: Nově lze tuto slevu uplatnit pouze v případě, že manžel/ka pečuje o dítě do 3 let věku ve společné domácnosti.',
    'Zrušení odpočtu za členské příspěvky odborům: Tento odpočet byl k 1. 1. 2024 zrušen.',
    'Zrušení odpočtu za úhradu zkoušek ověřujících výsledky dalšího vzdělávání: Tento odpočet byl k 1. 1. 2024 zrušen.',
]
</script>

<template>
    <AppLayout
        title="Slevy a odpočty"
        description="Slevy a odpočty snižují vypočítanou daň. Některé slevy mohou vést i k tzv. daňovému bonusu – stát poplatníkovi peníze vrátí, pokud sleva přesahuje vypočítanou daň."
    >
        <template #content>
            <section class="py-12 sm:py-24">
                <div class="mx-auto max-w-3xl px-6 lg:px-8">
                    <div class="max-w-2xl">
                        <HeadingTwo
                            class="!mt-0"
                            value="Slevy na dani"
                        />

                        <dl class="space-ytext-zinc-800 mt-6 text-zinc-800 dark:text-zinc-400">
                            <p>Slevy přímo snižují vypočítanou daň.</p>
                            <p>Mohou snížit daňovou povinnost až na nulu.</p>
                            <p>
                                Některé slevy (např. daňové zvýhodnění na dítě) mohou vést i k tzv. daňovému bonusu – stát poplatníkovi
                                peníze vrátí, pokud sleva přesahuje vypočítanou daň.
                            </p>
                        </dl>

                        <ul class="mt-6 space-y-4 text-zinc-800 dark:text-zinc-400">
                            <li
                                v-for="(discount, index) in taxDiscounts"
                                :key="index"
                            >
                                <HeadingThree> {{ discount.title }}: <br /> </HeadingThree>

                                <ul
                                    v-if="discount.amount"
                                    role="list"
                                    class="max-wtext-zinc-800 mt-5 ml-2 text-zinc-800 dark:text-zinc-400"
                                >
                                    <li class="font-bold text-zinc-800 underline dark:text-zinc-400">Výše:</li>

                                    <li class="gap-xtext-zinc-800 mt-2 flex text-zinc-800 dark:text-zinc-400">
                                        <CheckCircleIcon
                                            class="mt-1 size-5 flex-none text-cyan-600"
                                            aria-hidden="true"
                                        />

                                        {{ discount.amount }}
                                    </li>
                                </ul>

                                <ul
                                    v-if="discount.condition"
                                    role="list"
                                    class="max-wtext-zinc-800 mt-5 ml-2"
                                >
                                    <li class="font-bold text-zinc-800 underline dark:text-zinc-400">Podmínka:</li>

                                    <li class="gap-xtext-zinc-800 flex text-zinc-800 dark:text-zinc-400">
                                        <CheckCircleIcon
                                            class="mt-1 size-5 flex-none text-cyan-600"
                                            aria-hidden="true"
                                        />

                                        {{ discount.condition }}
                                    </li>
                                </ul>

                                <ul
                                    v-if="discount.conditions"
                                    role="list"
                                    class="max-wtext-zinc-800 mt-5 ml-2 text-zinc-800 dark:text-zinc-400"
                                >
                                    <li class="font-bold text-zinc-800 underline dark:text-zinc-400">Podmínka:</li>

                                    <li
                                        v-for="(condition, idx) in discount.conditions"
                                        :key="idx"
                                        class="gap-xtext-zinc-800 mb-1 flex text-zinc-800 dark:text-zinc-400"
                                    >
                                        <CheckCircleIcon
                                            class="mt-1 size-5 flex-none text-cyan-600"
                                            aria-hidden="true"
                                        />

                                        {{ condition }}
                                    </li>
                                </ul>

                                <ul
                                    v-if="discount.amounts"
                                    role="list"
                                    class="max-wtext-zinc-800 mt-5 ml-2 text-zinc-800 dark:text-zinc-400"
                                >
                                    <li class="font-bold text-zinc-800 underline dark:text-zinc-400">Výše:</li>

                                    <li
                                        v-for="(amount, idx) in discount.amounts"
                                        :key="idx"
                                        class="gap-xtext-zinc-800 mb-1 flex text-zinc-800 dark:text-zinc-400"
                                    >
                                        <CheckCircleIcon
                                            class="mt-1 size-5 flex-none text-cyan-600"
                                            aria-hidden="true"
                                        />

                                        {{ amount }}
                                    </li>
                                </ul>

                                <p class="mttext-zinc-800 text-zinc-800 dark:text-zinc-400">
                                    Pokud daňové zvýhodnění přesáhne výši vypočítané daně, vzniká daňový bonus – stát peníze vrátí
                                    poplatníkovi.
                                </p>
                                <p class="mttext-zinc-800 text-zinc-800 dark:text-zinc-400">
                                    <strong>Příklad:</strong> Pokud daňové zvýhodnění přesáhne výši vypočítané daně, vzniká daňový bonus –
                                    stát peníze vrátí poplatníkovi.
                                </p>
                            </li>
                        </ul>
                    </div>

                    <div class="max-w-2xl">
                        <HeadingTwo
                            class="mt-10"
                            value="Nezdanitelné části základu daně (odpočty)"
                        />

                        <dl class="space-ytext-zinc-800 mt-6 text-zinc-800 dark:text-zinc-400">
                            <p>Tyto položky snižují základ daně, tedy částku, ze které se daň počítá (15 % nebo 23 %).</p>
                            <p>Nevzniká z nich nárok na bonus, ale díky nim se snižuje samotná daň.</p>
                        </dl>

                        <ul class="space-ytext-zinc-800 mt-6 text-zinc-800 dark:text-zinc-400">
                            <li
                                v-for="(deduction, index) in taxDeductions"
                                :key="index"
                            >
                                <HeadingThree> {{ deduction.title }}: <br /> </HeadingThree>

                                <ul
                                    v-if="deduction.amount"
                                    role="list"
                                    class="max-wtext-zinc-800 mt-5 ml-2 text-zinc-800 dark:text-zinc-400"
                                >
                                    <li class="font-bold text-zinc-800 underline dark:text-zinc-400">Výše:</li>

                                    <li class="gap-xtext-zinc-800 flex text-zinc-800 dark:text-zinc-400">
                                        <CheckCircleIcon
                                            class="mt-1 size-5 flex-none text-cyan-600"
                                            aria-hidden="true"
                                        />

                                        {{ deduction.amount }}
                                    </li>
                                </ul>

                                <ul
                                    v-if="deduction.condition"
                                    role="list"
                                    class="max-wtext-zinc-800 mt-5 ml-2 text-zinc-800 dark:text-zinc-400"
                                >
                                    <li class="font-bold text-zinc-800 underline dark:text-zinc-400">Podmínka:</li>

                                    <li class="gap-xtext-zinc-800 flex text-zinc-800 dark:text-zinc-400">
                                        <CheckCircleIcon
                                            class="mt-1 size-5 flex-none text-cyan-600"
                                            aria-hidden="true"
                                        />

                                        {{ deduction.condition }}
                                    </li>
                                </ul>

                                <ul
                                    v-if="deduction.conditions"
                                    role="list"
                                    class="max-wtext-zinc-800 mt-5 ml-2 text-zinc-800 dark:text-zinc-400"
                                >
                                    <li class="font-bold text-zinc-800 underline dark:text-zinc-400">Podmínka:</li>

                                    <li
                                        v-for="(condition, idx) in deduction.conditions"
                                        :key="idx"
                                        class="gap-xtext-zinc-800 mb-1 flex text-zinc-800 dark:text-zinc-400"
                                    >
                                        <CheckCircleIcon
                                            class="mt-1 size-5 flex-none text-cyan-600"
                                            aria-hidden="true"
                                        />

                                        {{ condition }}
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>

                    <div class="max-w-2xl">
                        <HeadingTwo
                            class="mt-10"
                            value="Změny platné od roku 2024"
                        />

                        <ul class="mt-5 ml-2 space-y-4">
                            <li
                                v-for="(item, index) in changes2024"
                                :key="index"
                                class="gap-xtext-zinc-800 flex"
                            >
                                <span class="text-zinc-800 dark:text-zinc-400">
                                    <strong class="font-semibold text-black dark:text-zinc-300">{{ item.split(': ')[0] }}:</strong>
                                    {{ item.split(': ')[1] }}
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>
        </template>
    </AppLayout>
</template>
